import React, { useState } from 'react';
import './ConvertUserModal.css';

const ConvertUserModal = ({ users, onClose, onConvertSingle, onConvertMultiple }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('');

    // Filtrar usuarios
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = !filterRole || user.rol === filterRole;
        return matchesSearch && matchesRole;
    });

    // Manejar selección individual
    const handleUserSelect = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    // Seleccionar/deseleccionar todos
    const handleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(user => user.id));
        }
    };

    // Convertir usuario individual
    const handleSingleConvert = (userId) => {
        onConvertSingle(userId);
    };

    // Convertir usuarios seleccionados
    const handleMultipleConvert = () => {
        if (selectedUsers.length === 0) {
            alert('Selecciona al menos un usuario');
            return;
        }

        if (window.confirm(`¿Convertir ${selectedUsers.length} usuarios a partners?`)) {
            onConvertMultiple(selectedUsers);
            setSelectedUsers([]);
        }
    };

    const getRoleBadge = (role) => {
        const roleColors = {
            'admin': 'convert-user-modal-badge--admin',
            'proveedor': 'convert-user-modal-badge--provider',
            'comerciante': 'convert-user-modal-badge--merchant',
            'moderador': 'convert-user-modal-badge--moderator',
            'soporte': 'convert-user-modal-badge--support'
        };

        return (
            <span className={`convert-user-modal-role-badge ${roleColors[role] || 'convert-user-modal-badge--default'}`}>
                {role.toUpperCase()}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const uniqueRoles = [...new Set(users.map(user => user.rol))];

    return (
        <div className="convert-user-modal-overlay">
            <div className="convert-user-modal">
                <div className="convert-user-modal-header">
                    <h2>👥 Convertir Usuarios a Partners</h2>
                    <button className="convert-user-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="convert-user-modal-content">
                    {/* Controles de filtro */}
                    <div className="convert-user-modal-controls">
                        <div className="convert-user-modal-search-container">
                            <input
                                type="text"
                                placeholder="🔍 Buscar por nombre o email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="convert-user-modal-search-input"
                            />
                        </div>

                        <div className="convert-user-modal-filter-container">
                            <select
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                                className="convert-user-modal-filter-select"
                            >
                                <option value="">Todos los roles</option>
                                {uniqueRoles.map(role => (
                                    <option key={role} value={role}>
                                        {role.toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Controles de selección múltiple */}
                    {filteredUsers.length > 0 && (
                        <div className="convert-user-modal-bulk-controls">
                            <div className="convert-user-modal-selection-info">
                                <label className="convert-user-modal-checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                        onChange={handleSelectAll}
                                    />
                                    <span className="convert-user-modal-checkmark"></span>
                                    Seleccionar todos ({filteredUsers.length})
                                </label>

                                {selectedUsers.length > 0 && (
                                    <span className="convert-user-modal-selected-count">
                                        {selectedUsers.length} seleccionados
                                    </span>
                                )}
                            </div>

                            {selectedUsers.length > 0 && (
                                <button
                                    onClick={handleMultipleConvert}
                                    className="convert-user-modal-btn-bulk-convert"
                                >
                                    🎯 Convertir {selectedUsers.length} usuarios
                                </button>
                            )}
                        </div>
                    )}

                    {/* Lista de usuarios */}
                    <div className="convert-user-modal-users-list">
                        {filteredUsers.length === 0 ? (
                            <div className="convert-user-modal-no-users">
                                <h3>👤 No se encontraron usuarios</h3>
                                <p>Todos los usuarios disponibles ya son partners o no coinciden con el filtro.</p>
                            </div>
                        ) : (
                            filteredUsers.map(user => (
                                <div key={user.id} className="convert-user-modal-user-item">
                                    <div className="convert-user-modal-user-select">
                                        <label className="convert-user-modal-checkbox-container">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(user.id)}
                                                onChange={() => handleUserSelect(user.id)}
                                            />
                                            <span className="convert-user-modal-checkmark"></span>
                                        </label>
                                    </div>

                                    <div className="convert-user-modal-user-info">
                                        <div className="convert-user-modal-user-main">
                                            <h4>{user.nombre}</h4>
                                            <p className="convert-user-modal-user-email">{user.email}</p>
                                        </div>

                                        <div className="convert-user-modal-user-details">
                                            <span className="convert-user-modal-user-phone">{user.telefono || 'Sin teléfono'}</span>
                                            {getRoleBadge(user.rol)}
                                            <span className="convert-user-modal-user-date">Registro: {formatDate(user.fechaRegistro)}</span>
                                        </div>
                                    </div>

                                    <div className="convert-user-modal-user-actions">
                                        <button
                                            onClick={() => handleSingleConvert(user.id)}
                                            className="convert-user-modal-btn-convert-single"
                                            title="Convertir a partner"
                                        >
                                            🎯 Convertir
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="convert-user-modal-footer">
                    <div className="convert-user-modal-footer-info">
                        <p>💡 Los partners podrán acceder a su dashboard y generar enlaces de referidos.</p>
                    </div>
                    <button onClick={onClose} className="convert-user-modal-btn-cancel">
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConvertUserModal;
